import axios, { AxiosRequestConfig } from 'axios';

import { topchefConfiguration } from './topchef-configuration.js'
import { Administrator, Lead, LeadCreation, LeadWithBill, LocHallBill, PublicLead } from './topchef.types.js'
import { apiRequest, getErrorMessage, apiSharedConfig } from './topchef.utils.js'
import { extractFileNameFromHeader, startFileDownload } from './download.js'
import { Place } from "shared/topchef.types";


const getLoggedIAdmin = (): Promise<Administrator> =>
  apiRequest('GET', `${topchefConfiguration.apiUrl}/api/backoffice/me`);


const withCountry = (url: string, country?: string): string =>
  country ? `${url}?country=${encodeURIComponent(country)}` : url;


const loadAllLeads = (country?: string): Promise<PublicLead[]> =>
  apiRequest('GET', withCountry(`${topchefConfiguration.apiUrl}/api/backoffice/leads/all`, country));

const loadLeadDetails = (leadId: string, country?: string): Promise<Lead> =>
  apiRequest('GET', withCountry(`${topchefConfiguration.apiUrl}/api/backoffice/leads/all/${leadId}`, country));

const loadLeadsToValidate = (country?: string): Promise<PublicLead[]> =>
  apiRequest('GET', withCountry(`${topchefConfiguration.apiUrl}/api/backoffice/leads/to-validate`, country));


const loadLeadToValidate = (leadId: string, country?: string): Promise<Lead> =>
  apiRequest('GET', withCountry(`${topchefConfiguration.apiUrl}/api/backoffice/leads/to-validate/${leadId}`, country));


const validateLead = (leadId: string, leadCreation: LeadCreation): Promise<void> => {
  const { country: _country, ...leadPayload } = (leadCreation as LeadCreation & { country?: string | null });
  return apiRequest('PUT', `${topchefConfiguration.apiUrl}/api/backoffice/leads/to-validate/${leadId}/validate`, leadPayload);
}


const refuseLead = (leadId: string, country?: string): Promise<void> =>
  apiRequest('PUT', `${topchefConfiguration.apiUrl}/api/backoffice/leads/to-validate/${leadId}/deny`, { country });


const loadBillsToValidate = (country?: string): Promise<Lead[]> =>
  apiRequest('GET', withCountry(`${topchefConfiguration.apiUrl}/api/backoffice/leads/bills-to-validate`, country));


const loadLeadWithBillToValidate = (leadId: string, country?: string): Promise<LeadWithBill> =>
  apiRequest('GET', withCountry(`${topchefConfiguration.apiUrl}/api/backoffice/leads/bills-to-validate/${leadId}`, country));

const loadSignedLeads = (country?: string): Promise<Lead[]> =>
  apiRequest('GET', withCountry(`${topchefConfiguration.apiUrl}/api/backoffice/leads/signed/`, country));

const loadActivedPlaces = (country?: string): Promise<Place[]> =>
  apiRequest('GET', withCountry(`${topchefConfiguration.apiUrl}/api/places/all`, country));

async function downloadBill(leadId: string): Promise<void> {
  
  const downloadConfig: AxiosRequestConfig = {
    ...apiSharedConfig,
    responseType: 'blob',
  };

  return await axios.get(topchefConfiguration.apiUrl + `/api/backoffice/leads/bills-to-validate/${leadId}/bill`, downloadConfig)
  .then((response) => {
    const filename = extractFileNameFromHeader(response.headers['content-disposition']) as string;
    startFileDownload(filename, response.data, response.headers['content-type']);
  })
  .catch( (error) => {
    const errorMessage:string = getErrorMessage(error);
    return Promise.reject(new Error(errorMessage));
  });
}


const validateBill = (leadId: string, bill: LocHallBill, country?: string): Promise<void> =>
  apiRequest('PUT', `${topchefConfiguration.apiUrl}/api/backoffice/leads/bills-to-validate/${leadId}/validate`, { ...bill, country });


const loadAvailableLeads = async (country?: string): Promise<PublicLead[]> => {
  try {
    const allLeads = await loadAllLeads(country);

    if (!Array.isArray(allLeads)) {
      console.error("❌ loadAllLeads n'a pas renvoyé un tableau :", allLeads);
      throw new Error("Erreur de récupération des leads");
    }

    return allLeads.filter(l => l.status === "available" || l.status === "followed");

  } catch (error) {
    console.error("❌ Erreur dans loadAvailableLeads :", error);
    return [];
  }
};


const loadUpdatedLead = (leadId: string, updatedData: LeadCreation, country?: string): Promise<void> =>
  apiRequest('PUT', `${topchefConfiguration.apiUrl}/api/backoffice/leads/all/${leadId}`, { ...updatedData, country });


const features = {
  getLoggedIAdmin,
  loadLeadsToValidate,
  loadLeadToValidate,
  validateLead,
  refuseLead,
  loadBillsToValidate,
  loadLeadWithBillToValidate,
  downloadBill,
  validateBill,
  loadAllLeads,
  loadLeadDetails,
  loadAvailableLeads,
  loadSignedLeads,
  loadActivedPlaces,
  loadUpdatedLead,
};

export default features;
